import Head from 'next/head';
import {
    isOkToLoad
} from '../../utils/isOkToLoad';
import {
    FACEBOOK_PIXEL_ID,
    MetaTrackingEventName,
    MetaTrackingTypeEnum
} from '../../lib/tracking/meta';
import { useContext, useMemo } from 'react';
import localeCode from 'locale-code';
import { normalizeCountry, normalizeEmail, normalizeName, normalizePhone, sha256Hash } from '../../lib/tracking/utils';
import { SNAP_PIXEL_ID, SnapEventName } from '../../lib/tracking/snapchat';
import SessionContext from '../../contexts/session';
import { TIKTOK_DIGITAL_PIXEL_ID } from '../../lib/tracking/tiktok';
import { NOT_LOADED_HINT } from '../../pages/_app';

const TrackingScripts = () => {
    const { user } = useContext(SessionContext);
    const geo_country = useMemo(() => {
        const locale = Intl.DateTimeFormat().resolvedOptions().locale;
        return localeCode.getCountryCode(locale);
    }, []);

    const snapInitParamsString = useMemo(() => {
        const params = {
            ...(user?.email && user?.email !== NOT_LOADED_HINT ? {user_hashed_email: sha256Hash(normalizeEmail(user.email))} : {}),
            ...(user?.phone ? {user_hashed_phone_number: sha256Hash(normalizePhone(user.phone))} : {}),
            ...(user?.givenName ? {firstname: sha256Hash(normalizeName(user.givenName))} : {}),
            ...(user?.familyName ? {lastname: sha256Hash(normalizeName(user.familyName))} : {}),
            ...(geo_country ? {geo_country: sha256Hash(normalizeCountry(geo_country))} : {})
        };

        return Object.entries(params).map(([key, value]) => `${key}: '${value}'`).join(', ');
    }, [user, geo_country]);
    return (
        <Head>
            {isOkToLoad && (
                <link
                    rel="preconnect"
                    href="https://dev.visualwebsiteoptimizer.com"
                />
            )}
            {isOkToLoad && (
                <script
                    id="vwoCode"
                    defer
                    dangerouslySetInnerHTML={{
                        __html: `
                window._vwo_code || (function() {
                var account_id=${process.env.NEXT_PUBLIC_VWO_ACCOUNT_ID},
                version=2.0,
                settings_tolerance=2000,
                hide_element='body',
                hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',
                /* DO NOT EDIT BELOW THIS LINE */
                f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(){if(!f){f=true;var e=d.getElementById('_vis_opt_path_hides');if(e)e.parentNode.removeChild(e)}},finished:function(){return f},load:function(e){var t=this.getSettings(),n=d.createElement('script'),i=this;if(t){n.textContent=t;d.getElementsByTagName('head')[0].appendChild(n);if(!w.VWO||VWO.caE){stT.removeItem(cK);i.load(e)}}else{n.fetchPriority='high';n.src=e;n.type='text/javascript';n.onerror=function(){w._vwo_code.finish()};d.getElementsByTagName('head')[0].appendChild(n)}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){w._vwo_code.finish();stT.removeItem(cK)},e);var t=d.currentScript,n=d.createElement('style'),i=this.hide_element(),r=t&&!t.async&&i?i+this.hide_element_style():'',c=d.getElementsByTagName('head')[0];n.setAttribute('id','_vis_opt_path_hides');v&&n.setAttribute('nonce',v.nonce);n.setAttribute('type','text/css');if(n.styleSheet)n.styleSheet.cssText=r;else n.appendChild(d.createTextNode(r));c.appendChild(n);this.load('https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version)}};w._vwo_code=code;code.init();})();(function(){var i=window;function t(){if(i._vwo_code){var e=t.hidingStyle=document.getElementById('_vis_opt_path_hides')||t.hidingStyle;if(!i._vwo_code.finished()&&!_vwo_code.libExecuted&&(!i.VWO||!VWO.dNR)){if(!document.getElementById('_vis_opt_path_hides')){document.getElementsByTagName('head')[0].appendChild(e)}requestAnimationFrame(t)}}}t()})();
                `
                    }}
                />
            )}
            {isOkToLoad && (
                <script
                    defer
                    dangerouslySetInnerHTML={{
                        __html: `
                        !function(f,b,e,v,n,t,s)
                        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                        n.queue=[];t=b.createElement(e);t.async=!0;
                        t.src=v;s=b.getElementsByTagName(e)[0];
                        s.parentNode.insertBefore(t,s)}(window, document,'script',
                        'https://connect.facebook.net/en_US/fbevents.js');
                        fbq('init', '${FACEBOOK_PIXEL_ID.DIGITAL}');
                        fbq('${MetaTrackingTypeEnum.TRACK}', '${MetaTrackingEventName.PAGE_VIEW}');
                    `
                    }}
                />
            )}
            {isOkToLoad && (
                <noscript>
                    <img
                        height="1"
                        width="1"
                        style={{
                            display: 'none'
                        }}
                        src={`https://www.facebook.com/tr?id=${FACEBOOK_PIXEL_ID.DIGITAL}&ev=PageView&noscript=1`}
                    />
                </noscript>
            )}
            {isOkToLoad && (
                <script
                    defer
                    dangerouslySetInnerHTML={{
                        __html: `
                            !function (w, d, t) {
                            w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
                            var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
                            ;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
                            ttq.load('${TIKTOK_DIGITAL_PIXEL_ID}');
                            ttq.page();
                            }(window, document, 'ttq');
                        `
                    }}
                />
            )}
            {isOkToLoad && (
                <script
                    defer
                    dangerouslySetInnerHTML={{
                        __html: `
                        (function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function()
                        {a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};
                        a.queue=[];var s='script';r=t.createElement(s);r.async=!0;
                        r.src=n;var u=t.getElementsByTagName(s)[0];
                        u.parentNode.insertBefore(r,u);})(window,document,
                        'https://sc-static.net/scevent.min.js');
                        snaptr('init', '${SNAP_PIXEL_ID}', {${snapInitParamsString}});
                        snaptr('track', '${SnapEventName.PAGE_VIEW}');
                    `
                    }}
                />
            )}
        </Head>
    );
};

export default TrackingScripts;
